/* eslint-disable no-undef */
import { API } from 'aws-amplify';

import TOR_STATUSES from './utils/TOR_STATUSES';
import parseDriverFilter from './utils/parseDriverFilter';
import { createTimeOffRequest, updateTimeOffRequest, deleteTimeOffRequest } from '../../graphql/mutations';
import { getTimeOffRequest, listTimeOffRequests, listTimeOffRequestsByEmployeeIdByStartDate } from '../../graphql/queries';
import { formatIsoToDate } from '../../utils';

const paginateQuery = async ({ query, variables, resultKey }) => {
  let allItems = [];
  let token;
  do {
    const response = await API.graphql({
      query,
      variables: { ...variables, nextToken: token },
    });
    const result = response?.data?.[resultKey];
    allItems = [...allItems, ...(result?.items || [])];
    token = result?.nextToken;
  } while (token);
  return allItems;
};

const buildQueryParams = (filter) => {
  if (filter.employeeId) {
    const employeeId = filter.employeeId.eq;
    const gsiFilter = { ...filter };
    delete gsiFilter.employeeId;

    // Use startDate as GSI sort key condition instead of a filter
    const startDateCondition = gsiFilter.startDate;
    if (startDateCondition) delete gsiFilter.startDate;

    return {
      query: listTimeOffRequestsByEmployeeIdByStartDate,
      resultKey: 'listTimeOffRequestsByEmployeeIdByStartDate',
      variables: {
        employeeId,
        ...(startDateCondition && { startDate: startDateCondition }),
        ...(Object.keys(gsiFilter).length > 0 && { filter: gsiFilter }),
        limit: 1000,
      },
    };
  }

  return {
    query: listTimeOffRequests,
    resultKey: 'listTimeOffRequests',
    variables: { filter, limit: 1000 },
  };
};

export const fetchTimeOffRequests = async (status, viewAll = false, driverFilter = '') => {
  try {
    const filter = { status: { eq: status } };

    const driverParsed = parseDriverFilter(driverFilter);
    if (driverParsed) {
      Object.assign(filter, driverParsed);
    }

    // If not viewAll, only show requests whose end date is today or later
    if (!viewAll) {
      const today = new Date().toISOString().split('T')[0];
      filter.endDate = { ge: today };
    }

    return await paginateQuery(buildQueryParams(filter));
  } catch (error) {
    console.error('Error fetching time off requests:', error);
    throw error;
  }
};

export const approveTimeOffRequest = async request => {
  try {
    const response = await API.graphql({
      query: updateTimeOffRequest,
      variables: {
        input: {
          id: request.id,
          status: TOR_STATUSES.APPROVED,
          approvedByUserId: request.approvedByUserId,
          approvedByUserName: request.approvedByUserName,
        },
      },
    });

    return response?.data?.updateTimeOffRequest;
  } catch (error) {
    console.error('Error approving time off request:', error);
    throw error;
  }
};

export const declineTimeOffRequest = async request => {
  try {
    const response = await API.graphql({
      query: updateTimeOffRequest,
      variables: {
        input: {
          id: request.id,
          status: TOR_STATUSES.DECLINED,
          reason: request.reason,
          serviceComment: request.serviceComment,
          declinedByUserId: request.declinedByUserId,
          declinedByUserName: request.declinedByUserName,
        },
      },
    });

    return response?.data?.updateTimeOffRequest;
  } catch (error) {
    console.error('Error declining time off request:', error);
    throw error;
  }
};

export const resetTimeOffRequest = async request => {
  try {
    const response = await API.graphql({
      query: updateTimeOffRequest,
      variables: {
        input: {
          id: request.id,
          status: TOR_STATUSES.PENDING_APPROVAL,
          resetByUserId: request.resetByUserId,
          resetByUserName: request.resetByUserName,
        },
      },
    });

    return response?.data?.updateTimeOffRequest;
  } catch (error) {
    console.error('Error resetting time off request:', error);
    throw error;
  }
};

export const exportTimeOffRequestsToCSV = async (status, viewAll = false, driverFilter = '') => {
  try {
    const filter = { status: { eq: status } };

    const driverParsed = parseDriverFilter(driverFilter);
    if (driverParsed) {
      Object.assign(filter, driverParsed);
    }

    if (!viewAll) {
      const today = new Date().toISOString().split('T')[0];
      filter.endDate = { ge: today };
    }

    const timeOffRequests = await paginateQuery(buildQueryParams(filter));

    const headers = [
      'Date Entered',
      'Driver Number',
      'Driver Name',
      'Client Name',
      'Client Number',
      'Start Date',
      'End Date',
      'Reason',
      'Driver Note',
      'Service Note',
      'Status',
    ];
    const csvRows = [headers.join(',')];

    timeOffRequests.forEach(request => {
      const row = [
        formatIsoToDate(request.createdAt),
        request.employeeId,
        request.employeeName,
        request.customerName || '',
        request.customerId || '',
        formatIsoToDate(request.startDate),
        formatIsoToDate(request.endDate),
        request.reason,
        request.employeeComment || '',
        request.serviceComment || '',
        request.status,
      ]
        .map(value => `"${value}"`)
        .join(',');

      csvRows.push(row);
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `time-off-requests-${status}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (error) {
    console.error('Error exporting time off requests:', error);
    throw error;
  }
};


const DRIVER_OPTIONS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let driverOptionsCache = null;
let driverOptionsCacheTime = 0;

const listDriverOptionsQuery = /* GraphQL */ `
  query ListDriverOptions($limit: Int, $nextToken: String) {
    listTimeOffRequests(limit: $limit, nextToken: $nextToken) {
      items {
        employeeId
        employeeName
      }
      nextToken
    }
  }
`;

export const fetchDriverOptions = async () => {
  const now = Date.now();
  if (driverOptionsCache && now - driverOptionsCacheTime < DRIVER_OPTIONS_CACHE_TTL) {
    return driverOptionsCache;
  }

  try {
    const allItems = await paginateQuery({
      query: listDriverOptionsQuery,
      variables: { limit: 1000 },
      resultKey: 'listTimeOffRequests',
    });

    const driverMap = new Map();
    allItems.forEach(item => {
      if ((item.employeeName || item.employeeId) && !driverMap.has(item.employeeId)) {
        const driverLabel = item.employeeName && item.employeeId
          ? `${item.employeeName} - ${item.employeeId}`
          : item.employeeName || `${item.employeeId}`;
        driverMap.set(item.employeeId, {
          value: driverLabel,
          label: driverLabel,
        });
      }
    });

    const result = Array.from(driverMap.values()).sort((a, b) => a.label.localeCompare(b.label));
    driverOptionsCache = result;
    driverOptionsCacheTime = now;
    return result;
  } catch (error) {
    console.error('Error fetching driver options:', error);
    throw error;
  }
};

export const fetchTimeOffRequest = async id => {
  try {
    const response = await API.graphql({
      query: getTimeOffRequest,
      variables: { id },
    });

    return response?.data?.getTimeOffRequest;
  } catch (error) {
    console.error('Error fetching time off request:', error);
    throw error;
  }
};

export const addTimeOffRequest = async input => {
  try {
    const response = await API.graphql({
      query: createTimeOffRequest,
      variables: { input },
    });

    return response?.data?.createTimeOffRequest;
  } catch (error) {
    console.error('Error creating time off request:', error);
    throw error;
  }
};

export const removeTimeOffRequest = async id => {
  try {
    const response = await API.graphql({
      query: deleteTimeOffRequest,
      variables: { input: { id } },
    });

    return response?.data?.deleteTimeOffRequest;
  } catch (error) {
    console.error('Error deleting time off request:', error);
    throw error;
  }
};

export const searchTimeOffRequests = async searchCriteria => {
  try {
    const filter = {};

    if (searchCriteria.driverNameNumber && searchCriteria.driverNameNumber !== 'All') {
      const driverParsed = parseDriverFilter(searchCriteria.driverNameNumber);
      if (driverParsed) {
        Object.assign(filter, driverParsed);
      }
    }

    if (searchCriteria.status) {
      filter.status = { eq: searchCriteria.status };
    }

    if (searchCriteria.startDateFrom && searchCriteria.startDateTo) {
      filter.startDate = {
        between: [searchCriteria.startDateFrom, searchCriteria.startDateTo],
      };
    } else if (searchCriteria.startDateFrom) {
      filter.startDate = { ge: searchCriteria.startDateFrom };
    } else if (searchCriteria.startDateTo) {
      filter.startDate = { le: searchCriteria.startDateTo };
    }

    if (searchCriteria.requestDateFrom && searchCriteria.requestDateTo) {
      filter.createdAt = {
        between: [
          `${searchCriteria.requestDateFrom}T00:00:00.000Z`,
          `${searchCriteria.requestDateTo}T23:59:59.999Z`,
        ],
      };
    } else if (searchCriteria.requestDateFrom) {
      filter.createdAt = { ge: `${searchCriteria.requestDateFrom}T00:00:00.000Z` };
    } else if (searchCriteria.requestDateTo) {
      filter.createdAt = { le: `${searchCriteria.requestDateTo}T23:59:59.999Z` };
    }

    return await paginateQuery(buildQueryParams(filter));
  } catch (error) {
    console.error('Error searching time off requests:', error);
    throw error;
  }
};
